import { ContactAttributeBaseResource, ContactAttributeFormResource } from "../../types/contactAttribute";
import {
	createFirstNameContactAttributeListHandler,
	createFirstNameContactAttributeProfileHandler,
} from "../column/firstNameContactAttributeHandler";
import {
	createLastNameContactAttributeListHandler,
	createLastNameContactAttributeProfileHandler,
} from "../column/lastNameContactAttributeHandler";
import {
	createCheckboxDataFieldContactAttributeListHandler,
	createCheckboxDataFieldContactAttributeProfileHandler,
} from "../dataField/checkboxDataFieldContactAttributeHandler";
import {
	createDateDataFieldContactAttributeListHandler,
	createDateDataFieldContactAttributeProfileHandler,
} from "../dataField/dateDataFieldContactAttributeHandler";
import {
	createDatetimeDataFieldContactAttributeListHandler,
	createDatetimeDataFieldContactAttributeProfileHandler,
} from "../dataField/datetimeDataFieldContactAttributeHandler";
import {
	createEmailDataFieldContactAttributeListHandler,
	createEmailDataFieldContactAttributeProfileHandler,
} from "../dataField/emailDataFieldContactAttributeHandler";
import {
	createLongTextDataFieldContactAttributeListHandler,
	createLongTextDataFieldContactAttributeProfileHandler,
} from "../dataField/longTextDataFieldContactAttributeHandler";
import {
	createMultiSelectFieldDataFieldContactAttributeListHandler,
	createMultiSelectFieldDataFieldContactAttributeProfileHandler,
} from "../dataField/multiSelectFieldDataFieldContactAttributeHandler";
import {
	createNumberDataFieldContactAttributeListHandler,
	createNumberDataFieldContactAttributeProfileHandler,
} from "../dataField/numberDataFieldContactAttributeHandler";
import {
	createPhoneDataFieldContactAttributeListHandler,
	createPhoneDataFieldContactAttributeProfileHandler,
} from "../dataField/phoneDataFieldContactAttributeHandler";
import {
	createSelectFieldDataFieldContactAttributeListHandler,
	createSelectFieldDataFieldContactAttributeProfileHandler,
} from "../dataField/selectFieldDataFieldContactAttributeHandler";
import {
	createTextDataFieldContactAttributeListHandler,
	createTextDataFieldContactAttributeProfileHandler,
} from "../dataField/textDataFieldContactAttributeHandler";
import {
	createTimeDataFieldContactAttributeListHandler,
	createTimeDataFieldContactAttributeProfileHandler,
} from "../dataField/timeDataFieldContactAttributeHandler";
import {
	createUrlDataFieldContactAttributeListHandler,
	createUrlDataFieldContactAttributeProfileHandler,
} from "../dataField/urlDataFieldContactAttributeHandler";
import { IContactAttributeHandler, IContactAttributeListHandler } from "../IContactAttributeHandler";
import {
	createAddressesContactAttributeListHandler,
	createAddressesContactAttributeProfileHandler,
} from "../relation/addressesContactAttributeProfileHandler";
import {
	createCompaniesContactAttributeListHandler,
	createCompaniesContactAttributeProfileHandler,
} from "../relation/companiesContactAttributeProfileHandler";
import {
	createEmailsContactAttributeListHandler,
	createEmailsContactAttributeProfileHandler,
} from "../relation/emailsContactAttributeProfileHandler";
import {
	createPhonesContactAttributeListHandler,
	createPhonesContactAttributeProfileHandler,
} from "../relation/phonesContactAttributeProfileHandler";
import {
	createPrimaryAddressContactAttributeListHandler,
	createPrimaryAddressContactAttributeProfileHandler,
} from "../relation/primaryAddressContactAttributeProfileHandler";
import {
	createPrimaryCompanyContactAttributeListHandler,
	createPrimaryCompanyContactAttributeProfileHandler,
} from "../relation/primaryCompanyContactAttributeProfileHandler";
import {
	createPrimaryEmailContactAttributeListHandler,
	createPrimaryEmailContactAttributeProfileHandler,
} from "../relation/primaryEmailContactAttributeProfileHandler";
import {
	createPrimaryPhoneContactAttributeListHandler,
	createPrimaryPhoneContactAttributeProfileHandler,
} from "../relation/primaryPhoneContactAttributeProfileHandler";

export type AnyProfileHandler = IContactAttributeHandler<any>;

export function mapContactAttributeToProfileHandler(attribute: ContactAttributeFormResource): AnyProfileHandler | null {
	const { id, value: originalValue, type, dataField: originalDataField } = attribute;
	const value = originalValue as any;
	const dataField = originalDataField as any;

	switch (type) {
		// Column
		case "FIRST_NAME":
			return createFirstNameContactAttributeProfileHandler(id, value);
		case "LAST_NAME":
			return createLastNameContactAttributeProfileHandler(id, value);
		case "LOCALE":
		case "CREATED_AT":
		case "UPDATED_AT":
			throw new Error(`Not implemented: ${type}`);
		// Relation
		case "PRIMARY_EMAIL":
			return createPrimaryEmailContactAttributeProfileHandler(id, value);
		case "PRIMARY_PHONE":
			return createPrimaryPhoneContactAttributeProfileHandler(id, value);
		case "PRIMARY_COMPANY":
			return createPrimaryCompanyContactAttributeProfileHandler(id, value);
		case "COMPANIES":
			return createCompaniesContactAttributeProfileHandler(id, value);
		case "EMAILS":
			return createEmailsContactAttributeProfileHandler(id, value);
		case "PHONES":
			return createPhonesContactAttributeProfileHandler(id, value);
		case "ADDRESSES":
			return createAddressesContactAttributeProfileHandler(id, value);
		case "PRIMARY_ADDRESS":
			return createPrimaryAddressContactAttributeProfileHandler(id, value);
		case "NON_PRIMARY_EMAILS":
		case "NON_PRIMARY_PHONES":
		case "NON_PRIMARY_COMPANIES":
		case "NON_PRIMARY_ADDRESSES":
		case "DISTRIBUTION_LISTS":
		case "INVITATION_LISTS":
		case "TENANTS":
		case "TAGS":
		case "APPLICATIONS":
		case "CONTACT_TYPE":
			return null;
		// DataField
		case "TEXT_DATA_FIELD":
			return createTextDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "LONG_TEXT_DATA_FIELD":
			return createLongTextDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "NUMBER_DATA_FIELD":
			return createNumberDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "URL_DATA_FIELD":
			return createUrlDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "CHECKBOX_DATA_FIELD":
			return createCheckboxDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "DATE_DATA_FIELD":
			return createDateDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "DATETIME_DATA_FIELD":
			return createDatetimeDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "TIME_DATA_FIELD":
			return createTimeDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "EMAIL_DATA_FIELD":
			return createEmailDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "PHONE_DATA_FIELD":
			return createPhoneDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "SELECT_FIELD_DATA_FIELD":
			return createSelectFieldDataFieldContactAttributeProfileHandler(id, value, dataField);
		case "MULTI_SELECT_FIELD_DATA_FIELD":
			return createMultiSelectFieldDataFieldContactAttributeProfileHandler(id, value, dataField);
	}
}
export function mapContactAttributeToListHandler(
	attribute: ContactAttributeBaseResource,
): IContactAttributeListHandler | null {
	const { id, type, dataField: originalDataField } = attribute;
	const dataField = originalDataField as any;

	switch (type) {
		// Column
		case "FIRST_NAME":
			return createFirstNameContactAttributeListHandler(id);
		case "LAST_NAME":
			return createLastNameContactAttributeListHandler(id);
		case "LOCALE":
		case "CREATED_AT":
		case "UPDATED_AT":
			throw new Error(`Not implemented: ${type}`);
		// Relation
		case "PRIMARY_EMAIL":
			return createPrimaryEmailContactAttributeListHandler(id);
		case "PRIMARY_PHONE":
			return createPrimaryPhoneContactAttributeListHandler(id);
		case "PRIMARY_COMPANY":
			return createPrimaryCompanyContactAttributeListHandler(id);
		case "PRIMARY_ADDRESS":
			return createPrimaryAddressContactAttributeListHandler(id);
		case "COMPANIES":
			return createCompaniesContactAttributeListHandler(id);
		case "NON_PRIMARY_COMPANIES":
			return createPrimaryCompanyContactAttributeListHandler(id);
		case "EMAILS":
			return createEmailsContactAttributeListHandler(id);
		case "PHONES":
			return createPhonesContactAttributeListHandler(id);
		case "ADDRESSES":
			return createAddressesContactAttributeListHandler(id);
		case "NON_PRIMARY_EMAILS":
		case "NON_PRIMARY_PHONES":
		case "NON_PRIMARY_ADDRESSES":
		case "DISTRIBUTION_LISTS":
		case "INVITATION_LISTS":
		case "TENANTS":
		case "TAGS":
		case "APPLICATIONS":
		case "CONTACT_TYPE":
			return null;
		// DataField
		case "TEXT_DATA_FIELD":
			return createTextDataFieldContactAttributeListHandler(id, dataField);
		case "LONG_TEXT_DATA_FIELD":
			return createLongTextDataFieldContactAttributeListHandler(id, dataField);
		case "NUMBER_DATA_FIELD":
			return createNumberDataFieldContactAttributeListHandler(id, dataField);
		case "URL_DATA_FIELD":
			return createUrlDataFieldContactAttributeListHandler(id, dataField);
		case "CHECKBOX_DATA_FIELD":
			return createCheckboxDataFieldContactAttributeListHandler(id, dataField);
		case "DATE_DATA_FIELD":
			return createDateDataFieldContactAttributeListHandler(id, dataField);
		case "DATETIME_DATA_FIELD":
			return createDatetimeDataFieldContactAttributeListHandler(id, dataField);
		case "TIME_DATA_FIELD":
			return createTimeDataFieldContactAttributeListHandler(id, dataField);
		case "EMAIL_DATA_FIELD":
			return createEmailDataFieldContactAttributeListHandler(id, dataField);
		case "PHONE_DATA_FIELD":
			return createPhoneDataFieldContactAttributeListHandler(id, dataField);
		case "SELECT_FIELD_DATA_FIELD":
			return createSelectFieldDataFieldContactAttributeListHandler(id, dataField);
		case "MULTI_SELECT_FIELD_DATA_FIELD":
			return createMultiSelectFieldDataFieldContactAttributeListHandler(id, dataField);
	}
}
